import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import { useStripeConnection } from './useStripeConnection';

/**
 * Hook to get the effective payment provider for the store
 * Checks store settings and available integrations
 * @returns {Object} Payment provider status
 */
export const usePaymentProvider = () => {
  const user = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);
  const storeSettings = useSelector((state: DefaultRootState) => 
    state.promiseData?.data?.userData?.storeSettings
  );

  // Check Stripe connection
  const stripeConnection = useStripeConnection();
  const hasStripe = stripeConnection.canProcessPayments;

  // Check PayStack connection
  const { data: paystackProfile } = useQuery({
    queryKey: ['paystackProfile'],
    queryFn: async () => {
      try {
        return await apis.getPayStackProfile();
      } catch (error: any) {
        if (error?.response?.status === 400 && error?.response?.data?.error === 'PayStack keys not found') {
          return { authorised: false, profile: null };
        }
        return { authorised: false, profile: null };
      }
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
    retry: false
  });
  const hasPayStack = paystackProfile && (paystackProfile.authorised === true || paystackProfile.profile);

  // Determine effective payment provider
  const effectiveProvider = useMemo(() => {
    const storeProvider = storeSettings?.paymentProvider;

    // If store settings specify a provider, use it if it's actually connected
    if (storeProvider === 'stripe' && hasStripe) {
      return 'stripe';
    }
    
    if (storeProvider === 'paystack' && hasPayStack) {
      return 'paystack';
    }

    // Fallback: use Stripe if available, then PayStack
    if (hasStripe) {
      return 'stripe';
    }
    
    if (hasPayStack) {
      return 'paystack';
    }

    return null;
  }, [storeSettings?.paymentProvider, hasStripe, hasPayStack]);

  return {
    effectiveProvider,
    hasStripe,
    hasPayStack,
    isConfigured: effectiveProvider !== null,
    availableProviders: [
      ...(hasStripe ? ['stripe'] : []),
      ...(hasPayStack ? ['paystack'] : [])
    ]
  };
};

